import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, differenceInCalendarDays } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { CalendarDays, Minus, Plus, ArrowRight, ArrowUpRight, ShieldCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/i18n";
import { BOOKING_URL, AIRBNB_URL } from "@/photos";

const MAX_GUESTS = 2;

export default function BookingBar() {
  const { lang, t } = useLang();
  const locale = lang === "it" ? itLocale : enUS;
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [months, setMonths] = useState(1);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const sync = () => setMonths(mq.matches ? 2 : 1);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const nights =
    range.from && range.to ? differenceInCalendarDays(range.to, range.from) : 0;

  const label = (d) =>
    d ? format(d, "d MMM yyyy", { locale }) : t.booking.selectDates;

  const submitBooking = () => {
    const params = new URLSearchParams();
    if (range.from) params.set("checkin", format(range.from, "yyyy-MM-dd"));
    if (range.to) params.set("checkout", format(range.to, "yyyy-MM-dd"));
    params.set("group_adults", String(guests));
    params.set("no_rooms", "1");
    params.set("group_children", "0");
    params.set("lang", lang);
    window.open(`${BOOKING_URL}?${params.toString()}`, "_blank", "noopener");
  };

  const submitAirbnb = () => {
    const params = new URLSearchParams();
    if (range.from) params.set("check_in", format(range.from, "yyyy-MM-dd"));
    if (range.to) params.set("check_out", format(range.to, "yyyy-MM-dd"));
    params.set("adults", String(guests));
    const qs = params.toString();
    window.open(qs ? `${AIRBNB_URL}?${qs}` : AIRBNB_URL, "_blank", "noopener");
  };

  const fieldCls =
    "flex w-full flex-col items-start gap-1 rounded-2xl px-4 py-3 text-left transition-colors duration-300 hover:bg-teal-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40";

  return (
    <div className="w-full" data-testid="booking-form">
      <div className="grid grid-cols-1 gap-2 rounded-[1.75rem] border border-white/50 bg-white/85 p-3 shadow-2xl shadow-stone-950/30 backdrop-blur-xl sm:grid-cols-2 md:grid-cols-[1fr_1fr_auto_auto] md:items-center">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={fieldCls} data-testid="check-in-date-input">
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                <CalendarDays className="h-3.5 w-3.5" />
                {t.booking.checkin}
              </span>
              <span className={`text-base font-semibold ${range.from ? "text-stone-900" : "text-stone-400"}`}>
                {label(range.from)}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto rounded-3xl border-teal-100 p-4 shadow-2xl">
            <DayPicker
              mode="range"
              numberOfMonths={months}
              selected={range}
              onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
              disabled={{ before: new Date() }}
              locale={locale}
              defaultMonth={range.from || new Date()}
            />
          </PopoverContent>
        </Popover>

        <Popover open={open && !!range.from ? true : undefined} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className={fieldCls}
              data-testid="check-out-date-input"
            >
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                <CalendarDays className="h-3.5 w-3.5" />
                {t.booking.checkout}
              </span>
              <span className={`text-base font-semibold ${range.to ? "text-stone-900" : "text-stone-400"}`}>
                {label(range.to)}
              </span>
            </button>
          </PopoverTrigger>
        </Popover>

        <div className={fieldCls} data-testid="guests-select">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
            {t.booking.guests}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="minus"
              data-testid="guests-decrement"
              onClick={() => setGuests((g) => Math.max(1, g - 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-stone-200 text-stone-600 transition-colors hover:border-pink-400 hover:text-pink-600"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-[4.5rem] text-center text-base font-semibold text-stone-900" data-testid="guests-count">
              {guests} {guests === 1 ? t.booking.guestOne : t.booking.guestMany}
            </span>
            <button
              type="button"
              aria-label="plus"
              data-testid="guests-increment"
              onClick={() => setGuests((g) => Math.min(MAX_GUESTS, g + 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-stone-200 text-stone-600 transition-colors hover:border-pink-400 hover:text-pink-600"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:col-span-2 md:col-span-1 md:h-full md:flex-row">
          <button
            type="button"
            onClick={submitBooking}
            data-testid="submit-booking-button"
            className="group flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-pink-600 px-6 py-4 text-base font-bold text-white shadow-xl shadow-pink-600/30 transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-pink-700 active:scale-100 md:h-full"
          >
            Booking.com
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            onClick={submitAirbnb}
            data-testid="submit-airbnb-button"
            className="group flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl border-2 border-stone-200 bg-white px-6 py-4 text-base font-bold text-stone-800 transition-[transform,border-color,color] duration-300 hover:scale-[1.03] hover:border-[#FF5A5F] hover:text-[#FF5A5F] active:scale-100 md:h-full"
          >
            Airbnb
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-2 text-xs text-white/70">
        <span className="flex items-center gap-1.5" data-testid="booking-secure-note">
          <ShieldCheck className="h-3.5 w-3.5 text-teal-300" />
          {t.booking.note}
        </span>
        {nights > 0 && (
          <span className="font-semibold text-pink-300" data-testid="booking-nights-count">
            {nights} {nights === 1 ? t.booking.night : t.booking.nights} · {t.booking.max}
          </span>
        )}
      </div>
    </div>
  );
}
